import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const UPLOAD_BASE = import.meta.env.VITE_UPLOAD_URL || API_BASE;

/** Detect if running inside a Capacitor native app (iOS/Android) */
export function isNativeApp(): boolean {
  return !!(window as any).Capacitor?.isNativePlatform?.();
}

/** Detect if running as an installed PWA (standalone / fullscreen) */
export function isPwa(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || (navigator as any).standalone === true; // Safari iOS
}

// Default request timeout (overridable per-call). Kept generous because file
// uploads and report exports can legitimately run long, but bounded so
// PWAs on a flaky cellular connection can't hang indefinitely.
const DEFAULT_API_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS) || 60_000;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: DEFAULT_API_TIMEOUT_MS,
});

// Navigation callback — set by App.vue to avoid importing router here (circular dep)
let navigateTo: ((path: string) => void) | null = null;
export function setApiNavigator(fn: (path: string) => void) { navigateTo = fn; }

/**
 * RBAC signals for rbac/refresh.ts (set there — the auth store can't be
 * imported here, it imports this module):
 *  - `version`: the X-Permissions-Version header of a successful response;
 *  - `denied`: a 403 PERMISSION_DENIED / MODULE_DISABLED answer.
 */
export interface PermissionsSignal { version?: string; denied?: boolean }
let permissionsSignalListener: ((signal: PermissionsSignal) => void) | null = null;
export function setPermissionsSignalListener(fn: ((signal: PermissionsSignal) => void) | null) { permissionsSignalListener = fn; }

/** Called when the session can't be refreshed, so the auth store forgets it too (not only localStorage). */
let sessionEndedHandler: (() => void) | null = null;
export function setSessionEndedHandler(fn: (() => void) | null) { sessionEndedHandler = fn; }

const PERMISSION_DENIED_CODES: ReadonlySet<string> = new Set(['PERMISSION_DENIED', 'MODULE_DISABLED']);

// Force logout: clear all auth state and redirect to the correct login page.
// Uses router navigation (via navigateTo callback) so Safari PWA stays in
// standalone mode instead of breaking out to the browser.
function forceLogout() {
  const userType = localStorage.getItem('userType');
  try {
    sessionEndedHandler?.();
  } catch {
    /* never block the redirect */
  }
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userType');

  const path = userType === 'superadmin' ? '/admin/login'
    : userType === 'client' ? '/client-login'
    : '/auth';

  if (navigateTo) {
    navigateTo(path);
  } else {
    window.location.href = path;
  }
}

// ── Session refresh ($S session-api contract §1) ─────────────────────────

/** Window event carrying `{ accessToken, refreshToken }` after every refresh; stores/auth.ts mirrors it (same name there). */
export const TOKENS_REFRESHED_EVENT = 'tasklic:tokens-refreshed';
/** Waits before the 2nd and 3rd attempt of a refresh that failed transiently. */
export const REFRESH_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];
const REFRESH_TIMEOUT_MS = 15_000;

/** No refresh token to send: as final as a server rejection. */
class NoRefreshTokenError extends Error {
  constructor() {
    super('No refresh token');
    this.name = 'NoRefreshTokenError';
  }
}

/**
 * The server's verdict that the session is over: a 401 / 403 answered with
 * the API's JSON error (REFRESH_REUSED, REFRESH_EXPIRED, REFRESH_NOT_FOUND,
 * REFRESH_INVALID, ACCOUNT_INACTIVE, an older server's UNAUTHORIZED, a
 * suspended company's FORBIDDEN), or no refresh token at all. Anything else
 * — no response, a timeout, 429, 5xx, a proxy's HTML page — is transient:
 * the session and the token are kept, and a later refresh retries with the
 * same token (the server recovers a rotation whose answer was lost).
 */
export function isDefinitiveRefreshRejection(error: unknown): boolean {
  if (error instanceof NoRefreshTokenError) return true;
  const response = (error as AxiosError | null | undefined)?.response;
  if (!response || (response.status !== 401 && response.status !== 403)) return false;
  const code = (response.data as { error?: { code?: unknown } } | null | undefined)?.error?.code;
  return typeof code === 'string' && code.length > 0;
}

function refreshEndpointFor(userType: string | null): string {
  if (userType === 'superadmin') return '/superadmin/refresh';
  if (userType === 'client') return '/auth/client/refresh';
  return '/auth/refresh';
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function runRefresh(): Promise<string> {
  for (let attempt = 0; ; attempt += 1) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) throw new NoRefreshTokenError();
    try {
      const response = await axios.post(
        `${API_BASE}${refreshEndpointFor(localStorage.getItem('userType'))}`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout: REFRESH_TIMEOUT_MS }
      );
      const { accessToken, refreshToken: nextRefreshToken } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', nextRefreshToken);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(TOKENS_REFRESHED_EVENT, { detail: { accessToken, refreshToken: nextRefreshToken } }));
      }
      return accessToken;
    } catch (error) {
      if (isDefinitiveRefreshRejection(error) || attempt >= REFRESH_RETRY_DELAYS_MS.length || isOffline()) throw error;
      await wait(REFRESH_RETRY_DELAYS_MS[attempt]);
    }
  }
}

let refreshInFlight: Promise<string> | null = null;

/**
 * Refresh the session: one request at a time for every caller (the 401
 * interceptor, the socket's re-authentication, the auth store). Resolves to
 * the new access token. A transient failure is retried (REFRESH_RETRY_DELAYS_MS)
 * and then rejected with the session kept; a definitive rejection signs out
 * (forceLogout, once) and rejects — test it with isDefinitiveRefreshRejection.
 */
export function refreshSession(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = runRefresh()
      .catch((error: unknown) => {
        if (isDefinitiveRefreshRejection(error)) forceLogout();
        throw error;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function bearerOf(config: InternalAxiosRequestConfig): string | null {
  const header = config.headers?.Authorization;
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
}

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add locale header
    const locale = localStorage.getItem('locale') || 'en';
    config.headers['Accept-Language'] = locale;

    // Add tenant subdomain header for multi-tenant support
    const subdomain = localStorage.getItem('tenantSubdomain');
    if (subdomain) {
      config.headers['x-tenant-subdomain'] = subdomain;
    }

    // Identify mobile app requests so backend can skip Turnstile (can't run in native WebView)
    if (isNativeApp()) {
      config.headers['X-Client-Platform'] = 'mobile';
    }

    // Let browser set Content-Type with boundary for FormData uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Resolve relative /storage/ and /uploads/ paths in API responses to full URLs.
 * On the web browser, relative paths resolve naturally via the current origin.
 * In Capacitor, the origin is capacitor://localhost so we must prepend the tenant domain.
 */
function resolveStorageUrls(data: any): any {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      if (typeof data[i] === 'string') {
        data[i] = resolveIfStoragePath(data[i]);
      } else if (typeof data[i] === 'object') {
        resolveStorageUrls(data[i]);
      }
    }
    return data;
  }
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (typeof val === 'string') {
      data[key] = resolveIfStoragePath(val);
    } else if (typeof val === 'object' && val !== null) {
      resolveStorageUrls(val);
    }
  }
  return data;
}

function resolveIfStoragePath(val: string): string {
  // `/api/v1/files/p/…` is the signed private-file URL the API returns for
  // chat attachments, TDrive files and task / project / ticket attachments.
  // On cloud drivers it is relative, exactly like `/storage/…`, and needs
  // the same tenant-host treatment in a native build.
  if (!val.startsWith('/storage/') && !val.startsWith('/uploads/') && !val.startsWith('/api/v1/files/p/')) return val;

  /*
   * In a browser, leave the path relative.
   *
   * It resolves against the page's own origin, which is always the tenant the
   * person is actually looking at, and — the reason this matters — it keeps
   * previews same-origin. NGINX serves /storage with X-Frame-Options:
   * SAMEORIGIN, so a PDF pulled from a different host cannot be framed and the
   * viewer shows "refused to connect" instead of the document. The old
   * fallback to VITE_API_URL did exactly that whenever tenantSubdomain was
   * missing from localStorage, pointing previews at app.tasklic.com while the
   * page sat on a tenant subdomain.
   */
  if (!isNativeApp()) return val;

  // Capacitor has no useful origin to resolve against (capacitor://localhost),
  // so a native build needs the full tenant URL.
  const subdomain = localStorage.getItem('tenantSubdomain');
  if (subdomain) return `https://${subdomain}.tasklic.com${val}`;

  const base = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || '';
  return base ? `${base}${val}` : val;
}

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Auto-resolve storage URLs in all API responses
    if (response.data) {
      resolveStorageUrls(response.data);
    }
    // Permissions changed server-side? rbac/refresh.ts compares and refetches (debounced).
    const permissionsVersion = response.headers?.['x-permissions-version'];
    if (permissionsVersion && permissionsSignalListener) {
      permissionsSignalListener({ version: String(permissionsVersion) });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Auth endpoints legitimately return 401 (wrong password, expired
    // refresh token, etc.) and the page handler already shows an inline
    // error. Don't run our refresh-or-logout flow for these — that's what
    // was kicking users back to the sign-in page on a wrong password.
    const url = originalRequest.url || '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/client/login') ||
      url.includes('/auth/client/refresh') ||
      url.includes('/superadmin/login') ||
      url.includes('/superadmin/refresh');

    // Handle 401 errors (token expired) — but not on auth endpoints.
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Sent with a token that a refresh has replaced since: retry with the
      // current one rather than rotating again.
      const current = localStorage.getItem('accessToken');
      const sent = bearerOf(originalRequest);
      if (current && sent && sent !== current) {
        originalRequest.headers.Authorization = `Bearer ${current}`;
        return api(originalRequest);
      }

      try {
        const newAccessToken = await refreshSession();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Definitive: refreshSession() has signed out and is redirecting.
        if (isDefinitiveRefreshRejection(refreshError)) return new Promise(() => {});
        // Transient (offline, timeout, 5xx): keep the session; this request
        // fails, the next one (or the socket) refreshes again.
        return Promise.reject(refreshError);
      }
    }

    // A refusal may mean the UI is showing stale permissions: let rbac/refresh.ts refetch (throttled).
    if (error.response?.status === 403 && permissionsSignalListener) {
      const code = (error.response.data as { error?: { code?: unknown } } | undefined)?.error?.code;
      if (typeof code === 'string' && PERMISSION_DENIED_CODES.has(code)) permissionsSignalListener({ denied: true });
    }

    return Promise.reject(error);
  }
);

export default api;

// Export typed API methods
export const apiService = {
  // Generic methods
  get: api.get,
  post: api.post,
  put: api.put,
  patch: api.patch,
  delete: api.delete,

  // File upload helper — uses VITE_UPLOAD_URL to bypass Cloudflare size limits
  upload: async (url: string, file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post(url, formData, {
      baseURL: UPLOAD_BASE,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },

  // Multiple file upload — uses VITE_UPLOAD_URL to bypass Cloudflare size limits
  uploadMultiple: async (
    url: string,
    files: File[],
    fieldName = 'files',
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(fieldName, file);
    });

    return api.post(url, formData, {
      baseURL: UPLOAD_BASE,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },
};
